import { useQuery } from '@tanstack/react-query'
import { API_BASE } from '../utils'
import type { UserLeagueDetail, UserLeagueRanking, UserLeagueScoredMatch, UserLeagueSummary } from '../types/user-leagues'

// FastAPI's HTTPException bodies look like { "detail": "..." }. Falls back to
// the HTTP status if the body isn't in that shape.
async function errorMessage(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json()
    if (typeof body === 'object' && body !== null && typeof (body as { detail?: unknown }).detail === 'string') {
      return (body as { detail: string }).detail
    }
  } catch {
    // Body wasn't JSON — fall through to the status-based message.
  }
  return String(res.status)
}

async function fetchMyUserLeagues(token: string): Promise<UserLeagueSummary[]> {
  const res = await fetch(`${API_BASE}/user-leagues/me`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(await errorMessage(res))
  const json: { data: UserLeagueSummary[] } = await res.json()
  return json.data
}

export const useMyUserLeagues = (token: string | undefined) =>
  useQuery({
    queryKey: ['myUserLeagues'],
    queryFn: () => fetchMyUserLeagues(token as string),
    enabled: !!token,
    staleTime: 30 * 1000,
    retry: 1,
  })

async function fetchUserLeague(userLeagueId: number, token: string | undefined): Promise<UserLeagueDetail> {
  const res = await fetch(`${API_BASE}/user-leagues/${userLeagueId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  })
  if (!res.ok) throw new Error(await errorMessage(res))
  const json: { data: UserLeagueDetail } = await res.json()
  return json.data
}

export const useUserLeague = (userLeagueId: number | undefined, token: string | undefined) =>
  useQuery({
    queryKey: ['userLeague', userLeagueId, !!token],
    queryFn: () => fetchUserLeague(userLeagueId as number, token),
    enabled: !!userLeagueId,
    staleTime: 15 * 1000,
    retry: 1,
  })

async function fetchUserLeagueRankings(
  userLeagueId: number,
  startDate?: string,
  endDate?: string
): Promise<UserLeagueRanking[]> {
  const params = new URLSearchParams()
  if (startDate) params.set('start_date', startDate)
  if (endDate) params.set('end_date', endDate)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await fetch(`${API_BASE}/user-leagues/${userLeagueId}/rankings${qs}`)
  if (!res.ok) throw new Error(await errorMessage(res))
  const json: { data: UserLeagueRanking[] } = await res.json()
  return json.data
}

export const useUserLeagueRankings = (userLeagueId: number | undefined, startDate?: string, endDate?: string) =>
  useQuery({
    queryKey: ['userLeagueRankings', userLeagueId, startDate, endDate],
    queryFn: () => fetchUserLeagueRankings(userLeagueId as number, startDate, endDate),
    enabled: !!userLeagueId,
    staleTime: 15 * 1000,
    retry: 1,
  })

async function fetchUserLeagueScoredMatches(
  userLeagueId: number,
  profileId: string,
  startDate?: string,
  endDate?: string
): Promise<UserLeagueScoredMatch[]> {
  const params = new URLSearchParams()
  if (startDate) params.set('start_date', startDate)
  if (endDate) params.set('end_date', endDate)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await fetch(`${API_BASE}/user-leagues/${userLeagueId}/rankings/${profileId}/matches${qs}`)
  if (!res.ok) throw new Error(await errorMessage(res))
  const json: { data: UserLeagueScoredMatch[] } = await res.json()
  return json.data
}

export const useUserLeagueScoredMatches = (
  userLeagueId: number | undefined,
  profileId: string | undefined,
  enabled: boolean,
  startDate?: string,
  endDate?: string
) =>
  useQuery({
    queryKey: ['userLeagueScoredMatches', userLeagueId, profileId, startDate, endDate],
    queryFn: () => fetchUserLeagueScoredMatches(userLeagueId as number, profileId as string, startDate, endDate),
    enabled: enabled && !!userLeagueId && !!profileId,
    staleTime: 15 * 1000,
    retry: 1,
  })

export async function createUserLeague(
  token: string,
  payload: { name: string; start_date: string; end_date: string; league_ids: number[] }
): Promise<UserLeagueDetail> {
  const res = await fetch(`${API_BASE}/user-leagues`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await errorMessage(res))
  const json: { data: UserLeagueDetail } = await res.json()
  return json.data
}

export async function updateUserLeague(
  token: string,
  userLeagueId: number,
  payload: { end_date?: string; matches_start_date?: string; matches_end_date?: string; league_ids?: number[] }
): Promise<UserLeagueDetail> {
  const res = await fetch(`${API_BASE}/user-leagues/${userLeagueId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await errorMessage(res))
  const json: { data: UserLeagueDetail } = await res.json()
  return json.data
}

export async function joinUserLeague(token: string, leagueCode: string): Promise<UserLeagueDetail> {
  const res = await fetch(`${API_BASE}/user-leagues/join`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ league_code: leagueCode }),
  })
  if (!res.ok) throw new Error(await errorMessage(res))
  const json: { data: UserLeagueDetail } = await res.json()
  return json.data
}

export async function setUserLeagueMemberAdmin(
  token: string,
  userLeagueId: number,
  profileId: string,
  isAdmin: boolean
): Promise<void> {
  const res = await fetch(`${API_BASE}/user-leagues/${userLeagueId}/members/${profileId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_admin: isAdmin }),
  })
  if (!res.ok) throw new Error(await errorMessage(res))
}

export async function removeUserLeagueMember(token: string, userLeagueId: number, profileId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/user-leagues/${userLeagueId}/members/${profileId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(await errorMessage(res))
}

export async function deleteUserLeague(token: string, userLeagueId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/user-leagues/${userLeagueId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(await errorMessage(res))
}
